import { evaluateCondition } from "./evaluatePageLogic";
import { isOwnOptionValue } from "./ownOptionValue";
import type { FormAnswerMap, PageLogicCondition, PageLogicOperator } from "./types";

const OPERATORS: readonly PageLogicOperator[] = [
    "equals",
    "not_equals",
    "includes",
    "includes_own_option",
    "greater_than",
    "greater_than_or_equals",
    "less_than",
    "less_than_or_equals",
];

/**
 * An input that may carry `inputs.show_when` (one page-logic condition that must hold for it to be shown)
 * or `inputs.options_from` (its choices are the ones picked earlier; hidden when there are none).
 */
export interface ConditionalInput {
    input_id?: string | null;
    show_when?: unknown;
    options_from?: unknown;
}

/**
 * One entry of `inputs.options_from`:
 * - `{input_id, code}`: offer choice `code` when checkbox `input_id` is ticked.
 * - `{input_id, value, code}`: offer choice `code` when the answer to multiple-choice `input_id` includes `value`.
 * - `{input_id, own: true, code?}`: offer the respondent's own options picked in `input_id`, as themselves.
 *   `code` is what the SAS export writes for one of them (e.g. that brand's "Other" product).
 */
export type OptionsFromEntry =
    | { input_id: string; code: number }
    | { input_id: string; value: string; code: number }
    | { input_id: string; own: true; code?: number };

export { isOwnOptionValue };

/** Reads `inputs.options_from`. Malformed entries are dropped; null when there are none. */
export function parseOptionsFrom(raw: unknown): OptionsFromEntry[] | null {
    if (!Array.isArray(raw)) return null;
    const entries = raw.flatMap((entry): OptionsFromEntry[] => {
        if (!entry || typeof entry !== "object") return [];
        const { input_id, code, value, own } = entry as {
            input_id?: unknown;
            code?: unknown;
            value?: unknown;
            own?: unknown;
        };
        if (typeof input_id !== "string" || input_id === "") return [];
        if (own === true) {
            return [
                typeof code === "number" && Number.isFinite(code)
                    ? { input_id, own: true, code }
                    : { input_id, own: true },
            ];
        }
        if (typeof code !== "number" || !Number.isFinite(code)) return [];
        if (typeof value === "string" || typeof value === "number") return [{ input_id, value: String(value), code }];
        return [{ input_id, code }];
    });
    return entries.length > 0 ? entries : null;
}

function isTickedAnswer(value: FormAnswerMap[string]): boolean {
    return value === true || value === "true" || value === 1;
}

/** An answer as its list of values (as text): a multiple-choice list, or a single answer. */
function answerValues(value: FormAnswerMap[string]): string[] {
    if (Array.isArray(value)) return value.map(String);
    if (value === null || value === undefined || value === "" || typeof value === "boolean") return [];
    return [String(value)];
}

/**
 * The choice codes an options_from input offers for these answers: the codes whose checkbox is ticked or whose
 * value was picked, in options_from order, without repeats. Null for an input without options_from (all its
 * choices apply). Own options are not codes: see `allowedOwnOptionValues`.
 */
export function allowedOptionCodes(input: ConditionalInput, answers: FormAnswerMap): number[] | null {
    const entries = parseOptionsFrom(input.options_from);
    if (!entries) return null;
    const codes: number[] = [];
    for (const entry of entries) {
        if ("own" in entry) continue;
        const offered =
            "value" in entry
                ? answerValues(answers[entry.input_id]).includes(entry.value)
                : isTickedAnswer(answers[entry.input_id]);
        if (offered && !codes.includes(entry.code)) codes.push(entry.code);
    }
    return codes;
}

/**
 * The respondent's own options an options_from input offers: those picked in the inputs listed with
 * `own: true`, in options_from and answer order, without repeats. Null for an input without such entries.
 */
export function allowedOwnOptionValues(input: ConditionalInput, answers: FormAnswerMap): string[] | null {
    const sources = (parseOptionsFrom(input.options_from) ?? []).filter((entry) => "own" in entry);
    if (sources.length === 0) return null;
    const values: string[] = [];
    for (const source of sources) {
        for (const value of answerValues(answers[source.input_id])) {
            if (isOwnOptionValue(value) && !values.includes(value)) values.push(value);
        }
    }
    return values;
}

/**
 * Whether `value` (one answer, or a multiple-choice list) only uses choices an options_from input offers.
 * True for an input without options_from and for a blank answer.
 */
export function isOfferedAnswer(input: ConditionalInput, answers: FormAnswerMap, value: unknown): boolean {
    const codes = allowedOptionCodes(input, answers);
    if (!codes) return true;
    const offered = new Set([...codes.map(String), ...(allowedOwnOptionValues(input, answers) ?? [])]);
    return answerValues(value as FormAnswerMap[string]).every((picked) => offered.has(picked));
}

/** Whether the input's visibility depends on other answers (show_when or options_from). */
export function hasVisibilityCondition(input: ConditionalInput): boolean {
    return parseShowWhen(input.show_when) !== null || parseOptionsFrom(input.options_from) !== null;
}

/** Reads `inputs.show_when`. A missing or malformed condition means the input is always shown. */
export function parseShowWhen(raw: unknown): PageLogicCondition | null {
    if (!raw || typeof raw !== "object") return null;
    const condition = raw as Partial<PageLogicCondition>;
    if (typeof condition.input_id !== "string" || condition.input_id === "") return null;
    if (!condition.operator || !OPERATORS.includes(condition.operator)) return null;
    const value = condition.value;
    if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") return null;
    return { input_id: condition.input_id, operator: condition.operator, value };
}

/** Whether the input is shown for these answers (the current page's values plus earlier answers). */
export function isInputShown(input: ConditionalInput, answers: FormAnswerMap): boolean {
    const condition = parseShowWhen(input.show_when);
    if (condition && !evaluateCondition(condition, answers)) return false;
    const codes = allowedOptionCodes(input, answers);
    if (codes === null || codes.length > 0) return true;
    return (allowedOwnOptionValues(input, answers) ?? []).length > 0;
}

/**
 * Inputs hidden by their show_when condition, or by options_from when nothing they list was picked. An answer to a hidden input does not count, so an input
 * that depends on a hidden one is worked out without that answer (repeated until nothing changes).
 */
export function findHiddenInputIds(inputs: readonly ConditionalInput[], answers: FormAnswerMap): string[] {
    const conditional = inputs.filter((input) => input.input_id && hasVisibilityCondition(input));
    let hidden = new Set<string>();
    for (let round = 0; round <= conditional.length; round++) {
        const visibleAnswers = Object.fromEntries(Object.entries(answers).filter(([id]) => !hidden.has(id)));
        const next = new Set(
            conditional.filter((input) => !isInputShown(input, visibleAnswers)).map((input) => input.input_id!)
        );
        if (next.size === hidden.size && [...next].every((id) => hidden.has(id))) break;
        hidden = next;
    }
    return [...hidden];
}
